import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Washington Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const WALicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['24 LU biennial', 'Min 8 LU in Health, Safety & Welfare', '4 LU in Law and Professional Practice', 'Max 8 LU self-study', 'Effective 2023 renewal cycle'],
      stateBoard: 'Washington State Board of Architects',
      boardUrl: 'https://www.dol.wa.gov/business/architects/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '2 PDH in Ethics (first renewal)', 'Technical engineering subjects', 'Half-hour increments minimum', 'Max 15 PDH self-study'],
      stateBoard: 'Washington State Board of Registration for Engineers',
      boardUrl: 'https://www.dol.wa.gov/business/engineers/'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '2 PDH in Ethics (first renewal)', 'Land surveying-related subjects', 'Max 15 PDH self-study', 'Professional development activities'],
      stateBoard: 'Washington State Board of Registration for Engineers & Land Surveyors',
      boardUrl: 'https://www.dol.wa.gov/business/engineers/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 120,
      cycleYears: 3,
      terminology: 'CPE',
      highlights: ['120 CPE hours triennial', 'Min 4 hours Ethics every 3 years', 'Min 20 hours annually', 'Technical CPE for attest services', 'Calendar year basis (Jan 1 - Dec 31)'],
      stateBoard: 'Washington State Board of Accountancy',
      boardUrl: 'https://acb.wa.gov/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 1,
      terminology: 'N/A',
      highlights: ['Annual renewal', 'No continuing education requirement', 'Active practice or refresher course if inactive >5 years', 'Suicide prevention training (one-time)'],
      stateBoard: 'Washington State Nursing Care Quality Assurance Commission',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/NursingCommission'
    },
    {
      id: 'ARNP',
      name: 'Advanced Registered Nurse Practitioner',
      icon: '🩺',
      credits: 0,
      cycleYears: 1,
      terminology: 'N/A',
      highlights: ['Annual renewal', 'No formal CE requirement', 'Must maintain national certification', 'Suicide prevention training (one-time)', 'Active practice hours recommended'],
      stateBoard: 'Washington State Nursing Care Quality Assurance Commission',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/NursingCommission'
    },
    {
      id: 'LICSW',
      name: 'Licensed Independent Clinical Social Worker',
      icon: '🧠',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Assessment', 'Clinical social work practice', 'Evidence-based interventions'],
      stateBoard: 'Washington Department of Health - Social Work',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/ProfessionsNewReneworUpdate/SocialWorker'
    },
    {
      id: 'LCSW',
      name: 'Licensed Advanced Social Worker',
      icon: '🤝',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Assessment', 'Social work practice topics', 'Approved CE providers'],
      stateBoard: 'Washington Department of Health - Social Work',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/ProfessionsNewReneworUpdate/SocialWorker'
    },
    {
      id: 'MHC',
      name: 'Licensed Mental Health Counselor',
      icon: '💭',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Assessment', 'Mental health counseling topics', 'Clinical competency focus'],
      stateBoard: 'Washington Department of Health - Mental Health Counselors',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/ProfessionsNewReneworUpdate/MentalHealthCounselor'
    },
    {
      id: 'MFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 36,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['36 hours biennial', '6 hours Law and Ethics', '6 hours Suicide Assessment', 'Marriage and family therapy topics', 'Systems-based approaches'],
      stateBoard: 'Washington Department of Health - Marriage & Family Therapy',
      boardUrl: 'https://www.doh.wa.gov/LicensesPermitsandCertificates/ProfessionsNewReneworUpdate/MarriageandFamilyTherapist'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker',
      icon: '🏠',
      credits: 30,
      cycleYears: 2,
      terminology: 'clock hours',
      highlights: ['30 clock hours biennial', '3 hours Core Curriculum (Law & Rules)', '27 hours elective', 'In-person or distance learning', 'Clock hour = 50 minutes'],
      stateBoard: 'Washington Department of Licensing - Real Estate',
      boardUrl: 'https://www.dol.wa.gov/business/real-estate/'
    },
    {
      id: 'APPR',
      name: 'Certified Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial', '7-hour National USPAP Update (mandatory)', 'State-specific continuing education', 'AQB-approved providers', 'Must be relevant to appraisal practice'],
      stateBoard: 'Washington Department of Licensing - Real Estate Appraiser',
      boardUrl: 'https://www.dol.wa.gov/business/appraisers/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Washington Professional License Renewal Requirements",
      "description": "Complete guide to Washington professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/wa-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.dol.wa.gov/",
          "https://www.doh.wa.gov/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Washington Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": prof.credits > 0 
              ? `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`
              : `${prof.name} license renewal - maintain active status`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Washington License Renewal",
            "item": "https://cectrack.com/wa-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Washington professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Washington. Architects need 24 LU biennial, Engineers need 30 PDH biennial, CPAs need 120 CPE hours triennial, Social Workers need 36 hours biennial, Real Estate Brokers need 30 clock hours biennial. Nurses have no formal CE requirement but must maintain active practice."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Washington?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Washington professional licenses are overseen by the Department of Licensing (DOL) for Architects, Engineers, CPAs, Real Estate professionals, and the Department of Health (DOH) for healthcare professionals including Nurses and Social Workers."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Washington professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Washington professions renew biennially (every 2 years), including Architects, Engineers, Social Workers, and Real Estate Brokers. CPAs renew every 3 years, and Nurses renew annually."
          }
        },
        {
          "@type": "Question",
          "name": "Does Washington accept online CE courses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Washington accepts online continuing education courses from approved providers. However, some professions have limits on self-study hours. For example, Architects can use a maximum of 8 LU from self-study, and Engineers can use a maximum of 15 PDH from self-study."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Washington Department of Licensing",
      "url": "https://www.dol.wa.gov/",
      "areaServed": "Washington",
      "description": "Oversees professional licensing for Architects, Engineers, CPAs, Real Estate professionals, and other professions in Washington State"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Washington Department of Health",
      "url": "https://www.doh.wa.gov/",
      "areaServed": "Washington",
      "description": "Regulates healthcare professionals including Nurses, Social Workers, Counselors, and Therapists in Washington State"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Washington Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Washington professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, and more."
        keywords="Washington license renewal, WA CE requirements, WA continuing education, Washington professional licenses, WA DOL, WA DOH, Washington nurses, WA CPAs, WA engineers"
        canonical="https://cectrack.com/wa-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Washington Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Washington professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#065f46', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#065f46', marginBottom: '0.5rem' }} aria-label="Over 50 categories">50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#065f46', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>DOL/DOH Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#065f46', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Washington CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#d1fae5',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#065f46' }}>
                        {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No CE Required'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : `Every ${prof.cycleYears} Years`}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#10b981', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Washington License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Washington professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all WA professions',
                  'Upload certificates and match to requirements',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Washington professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Washington State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Washington professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Washington Department of Licensing (DOL)
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates Architects, Engineers, Land Surveyors, CPAs, Real Estate professionals, and Appraisers in Washington State.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+13606641400" aria-label="Call DOL at 360-664-1400">(360) 664-1400</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.dol.wa.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit DOL website">
                      www.dol.wa.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> PO Box 9020, Olympia, WA 98507
                  </div>
                  <a 
                    href="https://www.dol.wa.gov/business/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all DOL business licenses"
                  >
                    View All Business Licenses
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Washington Department of Health (DOH)
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates healthcare professionals including Nurses, Social Workers, Mental Health Counselors, and Marriage & Family Therapists.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+13602361100" aria-label="Call DOH at 360-236-1100">(360) 236-1100</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.doh.wa.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit DOH website">
                      www.doh.wa.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> PO Box 47877, Olympia, WA 98504
                  </div>
                  <a 
                    href="https://www.doh.wa.gov/LicensesPermitsandCertificates" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all DOH health professions"
                  >
                    View All Health Professions
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="WA" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Washington License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Washington who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default WALicenseRenewal;
